import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";

const links = [
  { label: "How it works", href: "#how-it-works", id: "how-it-works" },
  { label: "Differentiators", href: "#differentiators", id: "differentiators" },
  { label: "Proof", href: "#case-study", id: "case-study" },
  { label: "FAQ", href: "#faq", id: "faq" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const sections = links
      .map((l) => document.getElementById(l.id))
      .filter(Boolean);
    if (sections.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // Pick the entry with the largest intersection ratio
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) setActive(visible[0].target.id);
      },
      {
        rootMargin: "-30% 0px -55% 0px",
        threshold: [0, 0.25, 0.5, 0.75, 1],
      }
    );

    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  const handleNavClick = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      window.scrollTo({ top: el.offsetTop - 80, behavior: "smooth" });
      setActive(id);
      setOpen(false);
    }
  };

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#0B0D12]/80 backdrop-blur-2xl border-b border-[#2A2E3D]/60"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 h-20 flex items-center justify-between">
        <a
          href="#top"
          onClick={(e) => handleNavClick(e, "top")}
          data-testid="brand-logo"
          className="flex items-center gap-2 group"
        >
          <div className="relative overflow-hidden h-9 w-9 sm:h-10 sm:w-10 flex-shrink-0">
            <img
              src="/prospectivly-logo.svg"
              alt="Prospectivly"
              className="absolute max-w-none"
              style={{ height: "72px", width: "108px", left: "-28px", top: "-16px" }}
            />
          </div>
          <span className="font-sans not-italic uppercase text-2xl tracking-tight text-[#EDEDED]">
            prospectivly<span className="text-[#FF4500]">.</span>
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => {
            const isActive = active === l.id;
            return (
              <a
                key={l.href}
                href={l.href}
                onClick={(e) => handleNavClick(e, l.id)}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                data-active={isActive}
                className={`relative text-sm uppercase tracking-widest transition-colors ${
                  isActive
                    ? "text-[#FF4500]"
                    : "text-[#9CA3AF] hover:text-[#EDEDED]"
                }`}
              >
                {l.label}
                <span
                  className={`absolute left-0 -bottom-1 h-[2px] bg-[#FF4500] transition-all duration-300 ${
                    isActive ? "w-full" : "w-0"
                  }`}
                />
              </a>
            );
          })}
        </nav>

        <a
          href="#book"
          onClick={(e) => handleNavClick(e, "book")}
          data-testid="navbar-cta-button"
          className="hidden md:inline-flex items-center gap-2 bg-[#FF4500] hover:bg-[#E03C00] text-white px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 hover:shadow-[0_0_24px_rgba(255,69,0,0.35)]"
        >
          Book a demo <ArrowUpRight className="w-4 h-4" />
        </a>

        <button
          aria-label="Open menu"
          data-testid="mobile-menu-toggle"
          className="md:hidden p-2 text-[#EDEDED]"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="md:hidden bg-[#0B0D12]/95 backdrop-blur-2xl border-t border-[#2A2E3D]/60"
        >
          <div className="px-6 py-6 flex flex-col gap-5">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={(e) => handleNavClick(e, l.id)}
                className={`uppercase tracking-widest text-sm ${
                  active === l.id ? "text-[#FF4500]" : "text-[#EDEDED]"
                }`}
              >
                {l.label}
              </a>
            ))}
            <a
              href="#book"
              onClick={(e) => handleNavClick(e, "book")}
              className="inline-flex justify-center items-center gap-2 bg-[#FF4500] text-white px-5 py-3 rounded-full text-sm font-medium"
            >
              Book a demo <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      )}
    </motion.header>
  );
}
